import { create } from "zustand";
import type {
  BootstrapConfig,
  MfaRequiredLoginResponse,
  SessionUser,
  UserSecurityStatus,
} from "@gaole/shared";
import { api, clearAccessToken, refreshAccessToken, setTokens } from "../lib/api";
import { reconnectSocket } from "../lib/socket";

type LoginResult =
  | { ok: true }
  | { ok: false; mfaRequired: MfaRequiredLoginResponse };

interface AuthState {
  user: SessionUser | null;
  config: BootstrapConfig | null;
  userSecurity: UserSecurityStatus | null;
  sessionLocked: boolean;
  loading: boolean;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (challengeToken: string, code: string, method: "email" | "totp") => Promise<void>;
  loginWithPin: (email: string, pin: string) => Promise<void>;
  lockSession: () => void;
  unlockSession: () => void;
  fetchUserSecurity: () => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
  moduleEnabled: (key: string) => boolean;
}

function assertStaff(user: SessionUser): void {
  const isStaff = user.isSuperadmin || user.permissions.length > 0;
  if (!isStaff) {
    throw new Error("This account doesn't have backoffice access");
  }
}

function applyTokens(data: { accessToken: string; refreshToken: string; user: SessionUser }): void {
  assertStaff(data.user);
  setTokens(data.accessToken, data.refreshToken);
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  config: null,
  userSecurity: null,
  sessionLocked: false,
  loading: true,

  bootstrap: async () => {
    try {
      let config = await api<BootstrapConfig>("/config/bootstrap");
      if (!config.user && localStorage.getItem("gaole_bo_refresh")) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          config = await api<BootstrapConfig>("/config/bootstrap");
        }
      }
      set({ config, user: config.user, loading: false });
      if (config.user) {
        void get().fetchUserSecurity();
      }
    } catch {
      set({ loading: false });
    }
  },

  login: async (email, password) => {
    const data = await api<
      | MfaRequiredLoginResponse
      | { accessToken: string; refreshToken: string; user: SessionUser }
    >("/auth/login", {
      body: { email, password },
    });

    if ("status" in data && data.status === "mfa_required") {
      return { ok: false, mfaRequired: data };
    }

    const tokens = data as { accessToken: string; refreshToken: string; user: SessionUser };
    applyTokens(tokens);
    set({ user: tokens.user, sessionLocked: false });
    reconnectSocket();
    void get().bootstrap();
    return { ok: true };
  },

  verifyMfa: async (challengeToken, code, method) => {
    const data = await api<{ accessToken: string; refreshToken: string; user: SessionUser }>(
      "/auth/mfa/verify",
      { body: { challengeToken, code, method } },
    );
    applyTokens(data);
    set({ user: data.user, sessionLocked: false });
    reconnectSocket();
    void get().bootstrap();
  },

  loginWithPin: async (email, pin) => {
    const data = await api<{ accessToken: string; refreshToken: string; user: SessionUser }>(
      "/auth/pin/login",
      { body: { email, pin } },
    );
    applyTokens(data);
    set({ user: data.user, sessionLocked: false });
    reconnectSocket();
    void get().bootstrap();
  },

  lockSession: () => {
    clearAccessToken();
    set({ sessionLocked: true });
    reconnectSocket();
  },

  unlockSession: () => {
    set({ sessionLocked: false });
  },

  fetchUserSecurity: async () => {
    try {
      const userSecurity = await api<UserSecurityStatus>("/auth/me/security");
      set({ userSecurity });
    } catch {
      /* not signed in */
    }
  },

  logout: async () => {
    const refresh = localStorage.getItem("gaole_bo_refresh");
    await api("/auth/logout", { body: { refreshToken: refresh } }).catch(() => {});
    setTokens(null, null);
    set({ user: null, userSecurity: null, sessionLocked: false });
    reconnectSocket();
  },

  can: (permission) => {
    const user = get().user;
    if (!user) return false;
    if (user.isSuperadmin) return true;
    return user.permissions.includes(permission);
  },

  moduleEnabled: (key) => {
    const config = get().config;
    return config ? (config.modules as Record<string, boolean>)[key] !== false : true;
  },
}));
